/**
 * Datasheet calculated boxes and dropdown lists.
 *
 * A calculated box is an ordinary value box whose binding is `calc:<fn>(@<line>[,@<line>...])`.
 * `@506` is the first value box on DS_Line 506 of the same template, `@506.2` the second one on
 * that line by DS_RowOrder. A box on the calc's own sheet wins; otherwise the lowest sheet that
 * has the line. A calc may read another calc.
 *
 * A dropdown box is a user-entered box bound `dsvalues:list=<listName>`. It is stored in DSValues
 * exactly like plain `dsvalues`; only the Index Composer's input differs.
 *
 * The registry is a fixed set of named functions rather than a formula language, so nothing a
 * template author types is ever executed. It is implemented twice, here and in
 * PMD-API/PMD.API/Services/DsCalc.cs, because the Composer calculates live in the browser and the
 * package snapshot calculates on the server. Change both together; dsCalc.test.js and
 * DsCalcTests.cs carry the same golden vectors.
 *
 * Values: a calc result is a number, or text ('' blank, 'NA', 'Evaluate'). Calcs pass the raw
 * number to each other and round only for display, so a chain matches the workbook, which also
 * formats rather than rounds.
 */

export const DSVALUES = 'dsvalues';
const LIST_PREFIX = 'dsvalues:list=';
const CALC_PREFIX = 'calc:';

// ── Damper sizing ────────────────────────────────────────────────────────────

export const CFM_BANDS = ['< 1000', '1000-2500', '> 2500'];

// Torque factor per blade type, one per CFM band in CFM_BANDS order.
export const BLADE_FACTORS = {
  'Parallel Blade w/ Seals':  [7, 10.5, 14],
  'Parallel Blade w/o Seals': [4, 6, 8],
  'Opposed Blade w Seals':    [5, 7.5, 10],
  'Opposed Blade w/o Seals':  [3, 4.5, 6],
  'Round':                    [10, 14, 20],
};

// Recommended actuator sizes, smallest first; an estimate must be strictly below one to take it.
const TORQUE_STEPS = [180, 270, 360];

const NA = 'NA';
const EVALUATE = 'Evaluate';

const keyOf = v => String(v ?? '').trim().toLowerCase();
const isText = (v, t) => typeof v === 'string' && keyOf(v) === keyOf(t);

// Strict decimal: blank, units ('24"') and anything else non-numeric are not numbers.
const NUMERIC = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;
export function toNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v ?? '').trim();
  if (!NUMERIC.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function factorFor(blade, cfm) {
  const b = Object.keys(BLADE_FACTORS).find(k => keyOf(k) === keyOf(blade));
  const c = CFM_BANDS.findIndex(k => keyOf(k) === keyOf(cfm));
  return b && c >= 0 ? BLADE_FACTORS[b][c] : null;
}

// Blank (or non-numeric) inputs propagate as blank all the way down the chain; a lookup miss on
// a real area gives 'NA', which the recommendation reports as 'Evaluate'.
function areaSqIn(h, w) {
  const a = toNumber(h), b = toNumber(w);
  return a == null || b == null ? '' : a * b;
}
function areaSqFt(sqin) {
  const a = toNumber(sqin);
  return a == null ? '' : a / 144;
}
function torqueEst(blade, cfm, sqft) {
  if (isText(sqft, NA)) return NA;
  const a = toNumber(sqft);
  if (a == null) return '';
  const f = factorFor(blade, cfm);
  return f == null ? NA : f * a;
}
function torqueRec(sqft, est) {
  if (isText(sqft, NA)) return NA;
  if (toNumber(sqft) == null) return '';
  if (est === '' || est == null) return '';
  const e = toNumber(est);
  if (e == null) return EVALUATE;
  const step = TORQUE_STEPS.find(t => e < t);
  return step ?? EVALUATE;
}

/**
 * The function registry. `dp` is the display precision for a numeric result; null means the
 * result prints as it is. `args` labels each input for the box editor.
 */
export const CALC_FUNCTIONS = {
  'damper.areaSqIn':  { label: 'Damper area (sq in)',        args: ['Height (in)', 'Width (in)'],              dp: 0,    fn: areaSqIn },
  'damper.areaSqFt':  { label: 'Damper area (sq ft)',        args: ['Area (sq in)'],                           dp: 2,    fn: areaSqFt },
  'damper.torqueEst': { label: 'Estimated torque (in-lb)',   args: ['Blade type', 'Max CFM', 'Area (sq ft)'],  dp: 1,    fn: torqueEst },
  'damper.torqueRec': { label: 'Recommended torque (in-lb)', args: ['Area (sq ft)', 'Estimated torque'],       dp: null, fn: torqueRec },
};

export const CALC_LISTS = {
  'damper.bladeType': [...Object.keys(BLADE_FACTORS), NA],
  'damper.cfm':       [...CFM_BANDS, NA],
};

/** Runs a registry function on raw inputs. Unknown name or wrong arity -> null (unresolved). */
export function runCalc(name, inputs) {
  const def = CALC_FUNCTIONS[name];
  if (!def || inputs.length !== def.args.length) return null;
  return def.fn(...inputs);
}

/** A raw result as the sheet shows it. */
export function formatCalc(name, raw) {
  if (raw == null) return '';
  if (typeof raw !== 'number') return String(raw);
  const dp = CALC_FUNCTIONS[name]?.dp;
  return dp == null ? String(raw) : raw.toFixed(dp);
}

// ── Bindings ─────────────────────────────────────────────────────────────────

/** True for every user-entered binding: plain dsvalues and dsvalues:list=... alike. */
export function isDsValues(src) {
  return src === DSVALUES || (typeof src === 'string' && src.startsWith(`${DSVALUES}:`));
}

/** The dropdown list a dsvalues binding names, or null for a free-text one. */
export function listNameOf(src) {
  return typeof src === 'string' && src.startsWith(LIST_PREFIX) ? src.slice(LIST_PREFIX.length).trim() || null : null;
}

export function listBinding(listName) {
  return listName ? `${LIST_PREFIX}${listName}` : DSVALUES;
}

export function isCalc(src) {
  return typeof src === 'string' && src.startsWith(CALC_PREFIX);
}

const CALC_RE = /^calc:\s*([A-Za-z_][\w.]*)\s*\((.*)\)\s*$/;
const REF_RE  = /^@(\d+)(?:\.(\d+))?$/;

/** `calc:fn(@506,@507.2)` -> { fn, refs: [{ line: 506, nth: 1 }, { line: 507, nth: 2 }] }, or null. */
export function parseCalc(src) {
  if (!isCalc(src)) return null;
  const m = CALC_RE.exec(src.trim());
  if (!m) return null;
  const body = m[2].trim();
  const parts = body === '' ? [] : body.split(',').map(s => s.trim());
  const refs = [];
  for (const p of parts) {
    const r = REF_RE.exec(p);
    if (!r) return null;
    refs.push({ line: Number(r[1]), nth: r[2] ? Number(r[2]) : 1 });
  }
  return { fn: m[1], refs };
}

export function calcBinding(fn, refs) {
  return `${CALC_PREFIX}${fn}(${refs.map(r => (r.nth > 1 ? `@${r.line}.${r.nth}` : `@${r.line}`)).join(',')})`;
}

const MAX_DEPTH = 32;

/**
 * Evaluates every calc box of one template.
 *
 * boxes:     [{ id, type, lineNo, order, sheet }] - the whole template, every sheet
 * bindingOf: box -> its binding string on the side being shown
 * baseValue: box -> the display string of a non-calc box (a dsvalues entry or a resolved field)
 *
 * Returns { value(box) -> display text, raw(box), inputs(box) -> input display strings,
 * unresolved(box) }. A calc is unresolved when its function or a reference does not exist or
 * the chain is circular; it then shows blank. Blank inputs are not unresolved - blank is the
 * correct answer for an unfilled sheet.
 */
export function makeCalcEvaluator(boxes, bindingOf, baseValue) {
  const byLine = new Map();
  for (const b of boxes) {
    if (b.type !== 'value') continue;
    if (!byLine.has(b.lineNo)) byLine.set(b.lineNo, []);
    byLine.get(b.lineNo).push(b);
  }
  for (const list of byLine.values()) {
    list.sort((a, b) => (a.sheet ?? 1) - (b.sheet ?? 1) || (a.order ?? 1) - (b.order ?? 1));
  }

  const refBox = (ref, fromSheet) => {
    const list = byLine.get(ref.line) || [];
    const own = list.filter(b => (b.sheet ?? 1) === fromSheet);
    const pool = own.length > 0 ? own : list.filter(b => (b.sheet ?? 1) === (list[0]?.sheet ?? 1));
    return pool[ref.nth - 1] ?? null;
  };

  const memo = new Map();   // id -> { raw, fn, inputs, unresolved }
  const active = new Set();

  const evalBox = (box, depth) => {
    if (memo.has(box.id)) return memo.get(box.id);
    const parsed = parseCalc(bindingOf(box));
    const fail = { raw: '', fn: parsed?.fn ?? '', inputs: [], unresolved: true };
    if (!parsed || !CALC_FUNCTIONS[parsed.fn] || depth > MAX_DEPTH || active.has(box.id)) {
      if (!active.has(box.id)) memo.set(box.id, fail);
      return fail;
    }
    active.add(box.id);
    const raws = [];
    const inputs = [];
    let unresolved = false;
    for (const ref of parsed.refs) {
      const src = refBox(ref, box.sheet ?? 1);
      if (!src) { unresolved = true; raws.push(''); inputs.push(''); continue; }
      if (isCalc(bindingOf(src))) {
        const r = evalBox(src, depth + 1);
        if (r.unresolved) unresolved = true;
        raws.push(r.raw);
        inputs.push(formatCalc(r.fn, r.raw));
      } else {
        const v = baseValue(src) ?? '';
        raws.push(v);
        inputs.push(v);
      }
    }
    active.delete(box.id);
    const out = runCalc(parsed.fn, raws);
    const result = out == null || unresolved
      ? { ...fail, inputs }
      : { raw: out, fn: parsed.fn, inputs, unresolved: false };
    memo.set(box.id, result);
    return result;
  };

  return {
    raw:        box => evalBox(box, 0).raw,
    value:      box => { const r = evalBox(box, 0); return formatCalc(r.fn, r.raw); },
    inputs:     box => evalBox(box, 0).inputs,
    unresolved: box => evalBox(box, 0).unresolved,
  };
}
